import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { useWeb3React } from "@web3-react/core";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// Store
import { getTransactionLoading } from "../../store";
// Styled Components
import { StyledLoadingButton } from "../Buttons";
//import custom hook
import { TOAST_STATE } from "../../const";
import { triggerToast } from "../../utils";

type PrivateProps = {
  props: any;
};

type Button = {
  popup: boolean;
  quote: string;
  modal: {
    title: string;
    checkWallet: true;
    handleYes: (account?: string | null) => void;
  };
};

const modalBoxStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

function MoreButton({ props }: PrivateProps) {
  const { account } = useWeb3React();
  const navigate = useNavigate();
  const loading = useSelector(getTransactionLoading);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState<Button | null>(null);

  const handleMenuOpen = (e: React.MouseEvent<SVGSVGElement>) => {
    setMenuAnchor(e.currentTarget as unknown as HTMLElement);
  };
  const handleMenuClose = () => setMenuAnchor(null);

  const handleModalOpen = (button: Button) => {
    setSelectedButton(button);
    handleMenuClose();
    setOpen(true);
  };
  const handleModalClose = () => setOpen(false);

  return (
    <>
      {/* Trigger icon */}
      <MoreVertIcon
        sx={{
          color: "#6D6E8C",
          cursor: "pointer",
          borderRadius: "50%",
          p: 0.25,
          transition: "color 0.15s, background 0.15s",
          "&:hover": { color: "#64D3EF", bgcolor: "rgba(100,211,239,0.1)" },
        }}
        onClick={handleMenuOpen}
      />

      {/* Dropdown — portals to document.body, never clipped by table overflow */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 8,
          sx: {
            bgcolor: "#2D2E45",
            border: "1px solid #434460",
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            minWidth: 180,
            "& .MuiList-root": { py: 0.5 },
          },
        }}
      >
        {props.buttons.map((element: any, index: number) => {
          const isDisabled = !element.popup && !element.handleClick;
          const isApprove = element?.quote === "Approve";
          const isReject = element?.quote === "Reject" || element?.quote === "Suspend";
          const baseColor = isDisabled
            ? "#3A3B58"
            : isApprove ? "#03C295" : isReject ? "#ff6b6b" : "#C7C6C8";
          const hoverBg = isApprove
            ? "rgba(3,194,149,0.1)"
            : isReject
            ? "rgba(255,107,107,0.1)"
            : "rgba(100,211,239,0.08)";
          const hoverColor = isApprove ? "#03C295" : isReject ? "#ff6b6b" : "#64D3EF";

          return (
            <Box key={index}>
              <MenuItem
                disabled={isDisabled}
                onClick={
                  element.popup
                    ? () => handleModalOpen(element)
                    : () => { element.handleClick?.(navigate); handleMenuClose(); }
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  color: baseColor,
                  transition: "background 0.15s, color 0.15s",
                  "&:hover": { bgcolor: hoverBg, color: hoverColor },
                  "& svg": { fontSize: 18 },
                  "&.Mui-disabled": { opacity: 0.35, pointerEvents: "none" },
                }}
              >
                {element.icon}
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Raleway600", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  {element.quote}
                </Typography>
              </MenuItem>
              {index < props.buttons.length - 1 && (
                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", my: 0, mx: 1 }} />
              )}
            </Box>
          );
        })}
      </Menu>

      {/* Confirmation modal */}
      {selectedButton && (
        <Modal
          open={open}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              ...modalBoxStyle,
              bgcolor: "#2D2E45",
              border: "1px solid #434460",
              borderRadius: 3,
              boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
              p: 4,
              minWidth: 360,
              outline: "none",
            }}
          >
            {/* Close */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Box
                onClick={handleModalClose}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "#6D6E8C",
                  borderRadius: "50%",
                  p: 0.5,
                  transition: "color 0.15s, background 0.15s",
                  "&:hover": { color: "#EBEBF5", bgcolor: "rgba(255,255,255,0.06)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </Box>
            </Box>

            {/* Title */}
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                color: "#EBEBF5",
                fontFamily: "Raleway700",
                fontWeight: 700,
                mb: 3,
                textAlign: "center",
              }}
            >
              {selectedButton.modal.title}
            </Typography>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 3 }} />

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <button
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 8,
                  border: "1px solid #434460",
                  background: "transparent",
                  color: "#C7C6C8",
                  fontFamily: "Raleway700",
                  fontSize: 16,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.06)";
                  (e.currentTarget as HTMLButtonElement).style.color = "#EBEBF5";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                  (e.currentTarget as HTMLButtonElement).style.color = "#C7C6C8";
                }}
                onClick={handleModalClose}
              >
                No
              </button>
              <StyledLoadingButton
                loading={loading}
                className="!rounded-lg !font-raleway700 !text-base hover:opacity-80"
                style={{
                  flex: 1,
                  padding: "12px 0",
                  background: "linear-gradient(270deg, #6F87F9 0%, #64D3EF 100%)",
                  color: "#040826",
                  fontWeight: 700,
                }}
                onClick={async () => {
                  if (!account && selectedButton.modal.checkWallet)
                    return triggerToast("Please connect wallet firstly", TOAST_STATE.Info);

                  await selectedButton.modal.handleYes(account);
                  handleModalClose();
                }}
              >
                Yes
              </StyledLoadingButton>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
}

export default MoreButton;